import type { JSX } from 'react'
import { useAgents } from '../../../hooks/useAgents.js'
import { useContactCenterQueues } from '../../../hooks/useContactCenterQueues.js'
import { useNotificationTemplates } from '../../../hooks/useNotificationTemplates.js'
import type { FieldDef } from '../../../hooks/useRuleFields.js'
import type {
  NotifyAction,
  NotifyRecipient,
} from '../../../modules/admin/conversation-engine/types.js'
import Select from '../../ui/Select.js'

/**
 * Discriminated union of automation actions (UI-2c §0.B Phase 1).
 *
 * Mirrors the shapes the AutomationEngine consumes via
 *   apps/api/src/routes/conversations/admin/automation-rules.ts
 * (CreateBodySchema.actions is z.array(z.record(z.unknown())) — untyped at
 * the API boundary, so the engine's per-action handlers are the contract).
 *
 * Webhook deferred to Phase 2.
 */
export type AutomationAction =
  | {
      type: 'transfer'
      targetType: 'agent' | 'queue'
      targetId: string
      transferType: 'cold' | 'warm' | 'queue'
    }
  | { type: 'tag'; tag: string }
  | { type: 'set_custom_field'; fieldKey: string; value: unknown }
  | { type: 'close'; resolutionType: 'resolved' | 'merged' | 'cancelled' }
  | { type: 'require_approval'; approverAgentId?: string; approverQueueId?: string }
  // Notify shares the single source-of-truth shape with EscalationActionPicker
  // (UI-2d §0.3 — see modules/admin/conversation-engine/types.ts).
  | NotifyAction
  // AI auto-reply actions — control AI responses for the conversation
  | { type: 'enable_ai_conversation' }
  | { type: 'disable_ai_conversation' }

export interface AutomationActionPickerProps {
  value: AutomationAction[]
  onChange: (next: AutomationAction[]) => void
  /** Field catalog for the `set_custom_field` action's key dropdown. */
  fields: FieldDef[]
}

type ActionType = AutomationAction['type'] | 'webhook'

function blank(type: AutomationAction['type']): AutomationAction {
  switch (type) {
    case 'transfer':
      return {
        type: 'transfer',
        targetType: 'agent',
        targetId: '',
        transferType: 'cold',
      }
    case 'tag':
      return { type: 'tag', tag: '' }
    case 'set_custom_field':
      return { type: 'set_custom_field', fieldKey: '', value: '' }
    case 'close':
      return { type: 'close', resolutionType: 'resolved' }
    case 'require_approval':
      return { type: 'require_approval' }
    case 'notify':
      return {
        type: 'notify',
        templateId: '',
        recipient: { type: 'agent', id: '' },
      }
    case 'enable_ai_conversation':
      return { type: 'enable_ai_conversation' }
    case 'disable_ai_conversation':
      return { type: 'disable_ai_conversation' }
  }
}

export default function AutomationActionPicker({
  value,
  onChange,
  fields,
}: AutomationActionPickerProps): JSX.Element {
  const { agents } = useAgents()
  const { items: queues } = useContactCenterQueues()
  const { templates } = useNotificationTemplates({ audience: 'agent' })

  // Custom-field-only subset for the set_custom_field key dropdown.
  const customFields = fields.filter((f) => f.key.startsWith('custom.'))

  function updateAt(i: number, next: AutomationAction): void {
    onChange(value.map((a, idx) => (idx === i ? next : a)))
  }

  function removeAt(i: number): void {
    onChange(value.filter((_, idx) => idx !== i))
  }

  function addAction(): void {
    onChange([...value, blank('transfer')])
  }

  return (
    <div className="aap flex flex-col gap-2">
      {value.length === 0 && (
        <p className="text-sm text-gray-500">No actions configured.</p>
      )}
      {value.map((action, i) => (
        <div
          key={i}
          role="group"
          aria-label={`Automation action ${i + 1}`}
          className="aap-row flex flex-wrap items-center gap-2 rounded border border-gray-300 p-2"
        >
                    <Select
            ariaLabel={`Action type ${i + 1}`}
            size="sm"
            value={action.type}
            onChange={(v) => {
              const next = v as ActionType
              if (next === 'webhook') return // disabled — see option below
              updateAt(i, blank(next))
            }}
            disabled={true}
          >
            <option value="transfer">Transfer</option>
            <option value="tag">Tag</option>
            <option value="set_custom_field">Set custom field</option>
            <option value="close">Close</option>
            <option value="require_approval">Require approval</option>
            <option value="notify">Notify</option>
            <option value="enable_ai_conversation">Enable AI Conversation</option>
            <option value="disable_ai_conversation">Disable AI Conversation</option>
            <option
              value="webhook"
              disabled
              title="Coming in Phase 2"
            >
              Webhook (Coming in Phase 2)
            </option>
                    </Select>

          {action.type === 'transfer' && (
            <TransferEditor
              index={i}
              action={action}
              onChange={(next) => updateAt(i, next)}
              agents={agents}
              queues={queues}
            />
          )}

          {action.type === 'tag' && (
            <input
              aria-label={`Tag ${i + 1}`}
              type="text"
              value={action.tag}
              onChange={(e) => updateAt(i, { type: 'tag', tag: e.target.value })}
              className="rounded border border-gray-300 px-1 py-0.5 text-xs"
            />
          )}

          {action.type === 'set_custom_field' && (
            <>
                            <Select
                ariaLabel={`Custom field key ${i + 1}`}
                size="sm"
                value={action.fieldKey}
                onChange={(v) => updateAt(i, {
                    type: 'set_custom_field',
                    fieldKey: v,
                    value: action.value,
                  })}
              >
                <option value="">— select field —</option>
                {customFields.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
                            </Select>
              <input
                aria-label={`Custom field value ${i + 1}`}
                type="text"
                value={String(action.value ?? '')}
                onChange={(e) =>
                  updateAt(i, {
                    type: 'set_custom_field',
                    fieldKey: action.fieldKey,
                    value: e.target.value,
                  })
                }
                className="rounded border border-gray-300 px-1 py-0.5 text-xs"
              />
            </>
          )}

          {action.type === 'close' && (
                        <Select
              ariaLabel={`Resolution type ${i + 1}`}
              size="sm"
              value={action.resolutionType}
              onChange={(v) => updateAt(i, {
                  type: 'close',
                  resolutionType: v as 'resolved' | 'merged' | 'cancelled',
                })}
            >
              <option value="resolved">Resolved</option>
              <option value="merged">Merged</option>
              <option value="cancelled">Cancelled</option>
                        </Select>
          )}

          {action.type === 'require_approval' && (
            <RequireApprovalEditor
              index={i}
              action={action}
              onChange={(next) => updateAt(i, next)}
              agents={agents}
              queues={queues}
            />
          )}

          {action.type === 'notify' && (
            <NotifyEditor
              index={i}
              action={action}
              onChange={(next) => updateAt(i, next)}
              agents={agents}
              queues={queues}
              templates={templates}
            />
          )}

          {action.type === 'enable_ai_conversation' && (
            <span className="text-xs text-gray-600">
              AI will respond to all messages in this conversation using the knowledge base
            </span>
          )}

          {action.type === 'disable_ai_conversation' && (
            <span className="text-xs text-gray-600">
              AI auto-reply will be disabled for this conversation
            </span>
          )}

          <button
            type="button"
            aria-label={`Remove action ${i + 1}`}
            className="ml-auto rounded border border-gray-300 px-2 py-0.5 text-xs"
            onClick={() => removeAt(i)}
          >
            Remove
          </button>
        </div>
      ))}

      <div>
        <button
          type="button"
          className="rounded border border-gray-300 px-2 py-1 text-xs"
          onClick={addAction}
        >
          + Add action
        </button>
      </div>
    </div>
  )
}

interface AgentLike {
  id: string
  extension: string
}
interface QueueLike {
  id: string
  name: string
}

function TransferEditor({
  index,
  action,
  onChange,
  agents,
  queues,
}: {
  index: number
  action: Extract<AutomationAction, { type: 'transfer' }>
  onChange: (next: AutomationAction) => void
  agents: AgentLike[]
  queues: QueueLike[]
}): JSX.Element {
  const options = action.targetType === 'agent' ? agents : queues
  return (
    <>
            <Select
        ariaLabel={`Transfer target type ${index + 1}`}
        size="sm"
        value={action.targetType}
        onChange={(v) => onChange({
            ...action,
            targetType: v as 'agent' | 'queue',
            targetId: '',
          })}
      >
        <option value="agent">Agent</option>
        <option value="queue">Queue</option>
            </Select>
            <Select
        ariaLabel={`Transfer target ${index + 1}`}
        size="sm"
        value={action.targetId}
        onChange={(v) => onChange({ ...action, targetId: v })}
      >
        <option value="">— select —</option>
        {options.map((o) =>
          action.targetType === 'agent' ? (
            <option key={o.id} value={o.id}>
              {(o as AgentLike).extension}
            </option>
          ) : (
            <option key={o.id} value={o.id}>
              {(o as QueueLike).name}
            </option>
          ),
        )}
            </Select>
            <Select
        ariaLabel={`Transfer type ${index + 1}`}
        size="sm"
        value={action.transferType}
        onChange={(v) => onChange({
            ...action,
            transferType: v as 'cold' | 'warm' | 'queue',
          })}
      >
        <option value="cold">Cold</option>
        <option value="warm">Warm</option>
        <option value="queue">Queue</option>
            </Select>
    </>
  )
}

function RequireApprovalEditor({
  index,
  action,
  onChange,
  agents,
  queues,
}: {
  index: number
  action: Extract<AutomationAction, { type: 'require_approval' }>
  onChange: (next: AutomationAction) => void
  agents: AgentLike[]
  queues: QueueLike[]
}): JSX.Element {
  // Either-or: agent OR queue. Mode chooses which is populated.
  const mode: 'agent' | 'queue' = action.approverQueueId ? 'queue' : 'agent'
  return (
    <>
            <Select
        ariaLabel={`Approver type ${index + 1}`}
        size="sm"
        value={mode}
        onChange={(v) => {
          if (v === 'agent') {
            onChange({ type: 'require_approval', approverAgentId: '' })
          } else {
            onChange({ type: 'require_approval', approverQueueId: '' })
          }
        }}
      >
        <option value="agent">Agent</option>
        <option value="queue">Queue</option>
            </Select>
      {mode === 'agent' ? (
                <Select
          ariaLabel={`Approver agent ${index + 1}`}
          size="sm"
          value={action.approverAgentId ?? ''}
          onChange={(v) => onChange({
              type: 'require_approval',
              approverAgentId: v,
            })}
        >
          <option value="">— select agent —</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.extension}
            </option>
          ))}
                </Select>
      ) : (
                <Select
          ariaLabel={`Approver queue ${index + 1}`}
          size="sm"
          value={action.approverQueueId ?? ''}
          onChange={(v) => onChange({
              type: 'require_approval',
              approverQueueId: v,
            })}
        >
          <option value="">— select queue —</option>
          {queues.map((q) => (
            <option key={q.id} value={q.id}>
              {q.name}
            </option>
          ))}
                </Select>
      )}
    </>
  )
}

interface TemplateLike {
  id: string
  eventKey: string
}

const NOTIFY_ROLES: Array<'supervisor' | 'admin' | 'manager'> = [
  'supervisor',
  'admin',
  'manager',
]

function NotifyEditor({
  index,
  action,
  onChange,
  agents,
  queues,
  templates,
}: {
  index: number
  action: Extract<AutomationAction, { type: 'notify' }>
  onChange: (next: AutomationAction) => void
  agents: AgentLike[]
  queues: QueueLike[]
  templates: TemplateLike[]
}): JSX.Element {
  const recipientType = action.recipient.type

  function setRecipientType(t: string): void {
    let next: NotifyRecipient
    switch (t) {
      case 'agent':
        next = { type: 'agent', id: '' }
        break
      case 'queue':
        next = { type: 'queue', id: '' }
        break
      case 'role':
        next = { type: 'role', id: 'supervisor' }
        break
      case 'assignee':
        next = { type: 'assignee' }
        break
      case 'requester':
        next = { type: 'requester' }
        break
      default:
        return
    }
    onChange({ ...action, recipient: next })
  }

  return (
    <>
            <Select
        ariaLabel={`Notify template ${index + 1}`}
        size="sm"
        value={action.templateId}
        onChange={(v) => onChange({ ...action, templateId: v })}
      >
        <option value="">— select template —</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.eventKey}
          </option>
        ))}
            </Select>
            <Select
        ariaLabel={`Notify recipient type ${index + 1}`}
        size="sm"
        value={recipientType}
        onChange={(v) => setRecipientType(v)}
      >
        <option value="agent">Agent</option>
        <option value="queue">Queue</option>
        <option value="role">Role</option>
        <option value="assignee">Assignee</option>
        <option value="requester">Requester</option>
            </Select>
      {action.recipient.type === 'agent' && (
                <Select
          ariaLabel={`Notify agent ${index + 1}`}
          size="sm"
          value={action.recipient.id}
          onChange={(v) => onChange({
              ...action,
              recipient: { type: 'agent', id: v },
            })}
        >
          <option value="">— select agent —</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.extension}
            </option>
          ))}
                </Select>
      )}
      {action.recipient.type === 'queue' && (
                <Select
          ariaLabel={`Notify queue ${index + 1}`}
          size="sm"
          value={action.recipient.id}
          onChange={(v) => onChange({
              ...action,
              recipient: { type: 'queue', id: v },
            })}
        >
          <option value="">— select queue —</option>
          {queues.map((q) => (
            <option key={q.id} value={q.id}>
              {q.name}
            </option>
          ))}
                </Select>
      )}
      {action.recipient.type === 'role' && (
                <Select
          ariaLabel={`Notify role ${index + 1}`}
          size="sm"
          value={action.recipient.id}
          onChange={(v) => onChange({
              ...action,
              recipient: {
                type: 'role',
                id: v as 'supervisor' | 'admin' | 'manager',
              },
            })}
        >
          {NOTIFY_ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
                </Select>
      )}
    </>
  )
}
